import { createComponentPreviewEntry } from '@wix/editor-elements-integrations';
import {
  usePreviewEffect,
  usePreviewState,
  useResetComponent,
} from '@wix/editor-elements-preview-utils';
import type {
  IComponentPreviewWrapper,
  PreviewWrapperProps,
} from '@wix/editor-elements-types/thunderboltPreview';
import React from 'react';
import type {
  ICheckboxGroupProps,
  CheckboxGroupPreviewWrapperProps,
  ICheckboxGroupImperativeActions,
} from '../CheckboxGroup.types';
import styles from '../../Checkbox/viewer/style/CheckboxBasicSkin.scss';

const noop = () => {};

function withComponentPreview(
  WrappedComponent: React.ComponentType<ICheckboxGroupProps>,
): IComponentPreviewWrapper<
  ICheckboxGroupProps,
  CheckboxGroupPreviewWrapperProps
> {
  return React.forwardRef<ICheckboxGroupImperativeActions, ICheckboxGroupProps>(
    (
      {
        previewWrapperProps = {},
        setValidityIndication = noop,
        validateValue = noop,
        validateValueAndShowIndication = noop,
        ...viewerProps
      }: PreviewWrapperProps<
        ICheckboxGroupProps,
        CheckboxGroupPreviewWrapperProps
      >,
      ref,
    ) => {
      const { shouldResetComponent, componentViewMode, compPreviewState } =
        previewWrapperProps;

      usePreviewEffect({
        componentViewMode: componentViewMode || '',
        onPreviewViewMode: validateValue,
      });

      const key = useResetComponent({
        shouldResetComponent,
        id: viewerProps.id,
        onResetComponent: () => {
          setValidityIndication(false);
          validateValue();
        },
      });

      usePreviewState(viewerProps.id, compPreviewState, {
        focus: {
          selectors: `.${styles.input}`,
          type: 'plural',
        },
        error: {
          selectors: `.${styles.input}`,
          type: 'plural',
          state: 'invalid',
        },
        hover: {
          selectors: `.${styles.CheckboxBasicSkin}`,
          type: 'plural',
        },
        disabled: {
          selectors: `.${styles.CheckboxBasicSkin}`,
          type: 'plural',
        },
      });

      usePreviewState(viewerProps.id, compPreviewState);

      const forceValidityIndiction =
        componentViewMode === 'editor' && compPreviewState === 'error';

      const shouldShowValidityIndication =
        forceValidityIndiction || viewerProps.shouldShowValidityIndication;

      setValidityIndication =
        componentViewMode === 'editor' ? noop : setValidityIndication;

      return (
        <WrappedComponent
          {...viewerProps}
          validateValue={validateValue}
          validateValueAndShowIndication={validateValueAndShowIndication}
          setValidityIndication={setValidityIndication}
          shouldShowValidityIndication={shouldShowValidityIndication}
          key={key}
          ref={ref}
        />
      );
    },
  );
}

export default (ViewerComponent: React.ComponentType<ICheckboxGroupProps>) =>
  createComponentPreviewEntry(withComponentPreview(ViewerComponent));
